import React from 'react';
import { i18n } from 'config/locales';

import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { ReprimandModel } from 'forms/sections/history/employment/types';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import { getSafeDateTime } from 'helpers/date';
import ErrorMessage from 'components/ErrorMessage';
import Comments from 'components/Form/Comments';

interface Props
  extends ValidationElementProps,
  Partial<ReprimandModel>,
  Omit<AccordionItemProps, 'name'> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
}

export default class ReprimandItem extends ValidationElement<Props> {
  update = (queue: Partial<ReprimandModel>) => {
    this.props.onUpdate({
      Text: this.props.Text,
      Date: this.props.Date,
      Comments: this.props.Comments,
      ...queue,
    });
  };

  updateText = (values: ReprimandModel['Text']) => {
    this.update({
      Text: values,
    });
  };

  updateDate = (values: ReprimandModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateComments = (values: ReprimandModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  render() {
    const attr = `items[${this.props.$index}].Item`;
    const reprimandDate = getSafeDateTime(this.props.Date);
    const endEmploymentDate = getSafeDateTime(this.props.Dates?.to);

    return (
      <div>
        <Field
          attribute={setAttribute(this.props.attribute, `${attr}.Text`)}
          title={i18n.t('history.employment.default.reprimand.description.label')}
          titleSize="h4"
          className="explanation-left"
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={setAttribute(this.props.attribute, `${attr}.Text`)}
            name="Text"
            {...this.props.Text}
            onUpdate={this.updateText}
            onError={this.props.onError}
            maxlength="100"
            required={this.props.required}
          />
        </Field>
        { // Warning shown
          reprimandDate?.valid && endEmploymentDate?.valid && reprimandDate.datetime > endEmploymentDate.datetime && (
            <ErrorMessage
              isWarning
              title="Oops, there may be a problem"
              message={[
                'The date for this reprimand incident is after the end of this period of employment.',
                'Please ensure that the date entered is correct and that the incident happened on the date provided.'
              ]}
            />
          )
        }
        <Field
          attribute={setAttribute(this.props.attribute, `${attr}.Date`)}
          title={i18n.t('history.employment.default.reprimand.date.label')}
          titleSize="h4"
          className="date-left"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            attribute={setAttribute(this.props.attribute, `${attr}.Date`)}
            name="Date"
            {...this.props.Date}
            onUpdate={this.updateDate}
            minDateEqualTo
            maxDate={{ year: new Date().getFullYear() }}
            onError={this.props.onError}
            hideDay
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t(`history.employment.default.reprimand.Comments`)}
          name="Comments"
          attribute="ReprimandItemComments"
          className="optional-comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          onError={this.props.onError}
          required={false}
        />
      </div>
    );
  }
}

ReprimandItem.defaultProps = {
  Dates: {},
  Comments: {},
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
